/**
 * The time zone the portal writes times in.
 *
 * Every time shown to a customer is in one zone, so "Checked 9:14 am BST" means the same thing to the
 * owner in London and the teammate travelling in Dhaka. Which zone, first match wins:
 *  1. the person's own override (Settings → Account → "Show times in"), kept per user in this browser;
 *  2. the business's saved time zone (Settings → Business, `business_profiles.time_zone`);
 *  3. the browser's zone, until the business has chosen one.
 *
 * It is module state rather than React context because the formatters in `format.ts` are plain
 * functions called from ~160 places. `AppShell` keeps it in step with the profile and remounts the page
 * when the zone changes (see `useDisplayTimeZone`).
 */

const OVERRIDE_KEY = "cortexgrip.portal.time-zone.";

let businessZone: string | null = null;
let overrideZone: string | null = null;
let adoptedFor: string | null = null;
const listeners = new Set<() => void>();

/** True for an IANA zone this browser knows ("Europe/London"). */
export function isTimeZone(zone: string | null | undefined): zone is string {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** The viewer's own time zone, offered first when the business has none yet. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London";
  } catch {
    return "Europe/London";
  }
}

/** The zone times are written in right now. */
export function displayTimeZone(): string {
  return overrideZone ?? businessZone ?? browserTimeZone();
}

function change(update: () => void): void {
  const before = displayTimeZone();
  update();
  if (displayTimeZone() !== before) listeners.forEach((listener) => listener());
}

/**
 * The business's saved zone (null: not chosen yet, or one this browser doesn't know). Called while the
 * shell renders, so it tells nobody: the shell re-renders itself whenever the profile changes.
 */
export function setBusinessTimeZone(zone: string | null | undefined): void {
  businessZone = isTimeZone(zone) ? zone : null;
}

/** The person's own zone, or null to follow the business. Tells the shell, so it re-renders in the new zone. */
export function setTimeZoneOverride(zone: string | null | undefined): void {
  const next = isTimeZone(zone) ? zone : null;
  change(() => {
    overrideZone = next;
  });
}

/**
 * Takes up the choice this person saved in this browser, once per person. Called while the shell
 * renders, so it tells nobody; doing it once keeps a choice that couldn't be stored (private windows)
 * from being wiped by the next render.
 */
export function adoptStoredTimeZone(userId: string): void {
  if (adoptedFor === userId) return;
  adoptedFor = userId;
  overrideZone = loadTimeZoneOverride(userId);
}

export function subscribeTimeZone(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The override this person saved in this browser (null: follow the business). */
export function loadTimeZoneOverride(userId: string): string | null {
  try {
    const zone = localStorage.getItem(OVERRIDE_KEY + userId);
    return isTimeZone(zone) ? zone : null;
  } catch {
    return null;
  }
}

/** Saves (or, with null, clears) this person's override and applies it. */
export function saveTimeZoneOverride(userId: string, zone: string | null): void {
  try {
    if (zone) localStorage.setItem(OVERRIDE_KEY + userId, zone);
    else localStorage.removeItem(OVERRIDE_KEY + userId);
  } catch {
    /* private mode: applies until the page is reloaded */
  }
  setTimeZoneOverride(zone);
}

/** Test seam: back to "the browser's zone". */
export function resetTimeZone(): void {
  businessZone = null;
  overrideZone = null;
  adoptedFor = null;
}
